import {
  useState,
  useEffect,
  useCallback,
  useRef,
  PropsWithChildren,
} from "react";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { createHubConnection } from "@/utils/signalr";
import { HubType } from "@/services/negotiateService";
import {
  Notification,
  ConnectionState,
  UsersNotificationSubscriptionStatus,
  UsersNotificationType,
} from "@/models/notification/Notifications";
import { useServices } from "@/hooks/useServices";
import camelcaseKeys from "camelcase-keys";
import useUsers from "@/hooks/useUsers";
import { convertNotificationType } from "@/utils/notificationTexts";
import { useAuth0 } from "@auth0/auth0-react";

import useUserStore from "@/stores/user.store";
import {
  NotificationContext,
  NotificationHubContextProps,
} from "./NotificationHubContext";
import {
  parseWorkspaceCommentEntityPath,
  parseWorkspaceDocumentEntityPath,
} from "@/utils/entityPathParser";
import { useQueryClient } from "@tanstack/react-query";
import { PATH_KEYS } from "@/services/appServices";
import { logger } from "@/lib/logger";
import {
  invalidateDocumentHtml,
  invalidateMissingDocumentHtml,
} from "@/lib/documentHtmlQuery";

const NotificationHubProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { negotiate, setContext } = useServices().items.negotiate;
  const { me } = useUserStore();
  const { isAuthenticated } = useAuth0();
  const {
    useQueryActiveAndSeenNotifications,
    useQueryAllNotifications,
    markAllNotificationsAsSeen,
    markNotificationsAsSeen,
    dismissAllNotifications,
    dismissNotification,
    dismissNotifications,
    deleteNotifications,
    deleteAllNotifications,
  } = useUsers();
  const queryClient = useQueryClient();

  // State
  const [activeNotifications, setActiveNotifications] = useState<
    Notification[]
  >([]);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [totalNotificationCount, setTotalNotificationCount] =
    useState<number>(0);
  const [activeCount, setActiveCount] = useState<number>(0);
  const [isMarkingAsSeen, setIsMarkingAsSeen] = useState<{
    isMarking: boolean;
    prevCount: number;
  }>({ isMarking: false, prevCount: 0 });

  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Disconnected
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // const [error, setError] = useState<string | null>(null);
  const [isDismissingAllNotifications, setIsDismissingAllNotifications] =
    useState(false);
  const [isDismissingNotification, setIsDismissingNotification] = useState<
    Record<string, boolean> | undefined
  >(undefined);
  const [checkedNotifications, setCheckedNotifications] = useState<string[]>(
    []
  );
  const [isDeletingNotifications, setIsDeletingNotifications] = useState(false);
  const [isDeletingAllNotifications, setIsDeletingAllNotifications] = useState(false);

  // Pagination state for infinite scrolling
  const [allNotificationsCurrentPage, setAllNotificationsCurrentPage] =
    useState(1);
  const [activeNotificationsCurrentPage, setActiveNotificationsCurrentPage] =
    useState(1);
  const [hasMoreAll, setHasMoreAll] = useState(true);
  const [hasMoreActive, setHasMoreActive] = useState(true);
  const [isLoadingMoreAll, setIsLoadingMoreAll] = useState(false);
  const [isLoadingMoreActive, setIsLoadingMoreActive] = useState(false);

  // Fetch notifications
  const fetchActiveNotifications = useQueryActiveAndSeenNotifications({
    pageNumber: activeNotificationsCurrentPage,
    pageSize: 20,
  });
  const fetchAllNotifications = useQueryAllNotifications({
    pageNumber: allNotificationsCurrentPage,
    pageSize: 20,
  });

  // Add hubConnection state like ConversationHubProvider
  const [hubConnection, setHubConnection] = useState<HubConnection | null>(
    null
  );
  const connectionInitialized = useRef(false);

  // Fetch notifications from API and sync with context
  useEffect(() => {
    if (fetchActiveNotifications.isFetched && fetchActiveNotifications.data) {
      const convertedNotifications = fetchActiveNotifications.data.data.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (notification: any) => ({
          ...notification,
          type: convertNotificationType(notification.type),
        })
      );

      if (activeNotificationsCurrentPage === 1) {
        setActiveNotifications(convertedNotifications);
      } else {
        setActiveNotifications((prev) => {
          const existingIds = new Set(prev.map((n) => n.id));
          const newNotifications = convertedNotifications.filter(
            (n) => !existingIds.has(n.id)
          );
          return [...prev, ...newNotifications];
        });
      }
      setActiveCount(fetchActiveNotifications.data.activeCount ?? 0);
      setHasMoreActive(fetchActiveNotifications.data.hasMore || false);
    }
  }, [
    fetchActiveNotifications.isFetched,
    fetchActiveNotifications.data,
    activeNotificationsCurrentPage,
  ]);

  useEffect(() => {
    if (fetchAllNotifications.isFetched && fetchAllNotifications.data) {
      const apiNotifications = (fetchAllNotifications.data.data || []).map(
        (notification: Notification) => ({
          ...notification,
          type: convertNotificationType(notification.type),
        })
      );

      // Replace when on page 1 OR when goToAllNotificationsPage triggered the load
      if (allNotificationsCurrentPage === 1 || pendingPageReset.current) {
        pendingPageReset.current = false;
        setAllNotifications(apiNotifications);
      } else {
        setAllNotifications((prev) => {
          // Check for duplicates before appending
          const existingIds = new Set(prev.map((n) => n.id));
          const newNotifications = apiNotifications.filter(
            (n) => !existingIds.has(n.id)
          );
          return [...prev, ...newNotifications];
        });
      }

      setTotalNotificationCount(fetchAllNotifications.data.totalRecords ?? 0);
      // Update pagination state
      setHasMoreAll(fetchAllNotifications.data.hasMore || false);
    }
  }, [
    fetchAllNotifications.isFetched,
    fetchAllNotifications.data,
    allNotificationsCurrentPage,
  ]);

  // Handle loading state for infinite scroll
  useEffect(() => {
    if (!fetchAllNotifications.isFetching && isLoadingMoreAll) {
      setIsLoadingMoreAll(false);
    }
  }, [fetchAllNotifications.isFetching, isLoadingMoreAll]);

  useEffect(() => {
    if (!fetchActiveNotifications.isFetching && isLoadingMoreActive) {
      setIsLoadingMoreActive(false);
    }
  }, [fetchActiveNotifications.isFetching, isLoadingMoreActive]);

  // Used to signal that the next data fetch should replace (not append) allNotifications
  const pendingPageReset = useRef(false);

  // Load more notifications for infinite scrolling
  const loadMoreAllNotifications = useCallback(async () => {
    if (!hasMoreAll || isLoadingMoreAll) return;

    setIsLoadingMoreAll(true);
    try {
      const nextPage = allNotificationsCurrentPage + 1;
      setAllNotificationsCurrentPage(nextPage);
    } catch (error) {
      logger.error("Failed to load more notifications", {
        operation: "loadMoreAllNotifications",
        error,
        pageNumber: allNotificationsCurrentPage,
      });
      setIsLoadingMoreAll(false);
    }
  }, [hasMoreAll, isLoadingMoreAll, allNotificationsCurrentPage]);

  // Page-based navigation: replace the current page instead of appending
  const goToAllNotificationsPage = useCallback((page: number) => {
    pendingPageReset.current = true;
    setAllNotificationsCurrentPage(page);
  }, []);

  const PAGE_SIZE = 20;
  const allNotificationsTotalPages = Math.max(
    1,
    Math.ceil(totalNotificationCount / PAGE_SIZE)
  );

  const loadMoreActiveNotifications = useCallback(async () => {
    if (!hasMoreActive || isLoadingMoreActive) return;

    setIsLoadingMoreActive(true);
    try {
      const nextPage = activeNotificationsCurrentPage + 1;
      setActiveNotificationsCurrentPage(nextPage);
    } catch (error) {
      logger.error("Failed to load more notifications", {
        operation: "loadMoreActiveNotifications",
        error,
        pageNumber: activeNotificationsCurrentPage,
      });
      setIsLoadingMoreActive(false);
    }
  }, [hasMoreActive, isLoadingMoreActive, activeNotificationsCurrentPage]);

  const seeAllNotifications = async () => {
    // Read from this render, not from `isMarkingAsSeen.prevCount`: that closure
    // still holds the state from before the `setIsMarkingAsSeen` below.
    const previousCount = activeCount;
    try {
      // Set the previous count to the current count, incase that it fails, so we can revert to the previous count
      setIsMarkingAsSeen({ isMarking: true, prevCount: activeCount });
      setActiveCount(0);
      await markAllNotificationsAsSeen.mutateAsync();
    } catch (error) {
      setActiveCount(previousCount);
      logger.error("Failed to mark all notifications as seen", {
        operation: "markAllNotificationsAsSeen",
        error,
      });
    } finally {
      setIsMarkingAsSeen({ isMarking: false, prevCount: 0 });
    }
  };

  const seeNotifications = async (notificationIds: string[]) => {
    try {
      setIsMarkingAsSeen({ isMarking: true, prevCount: activeCount });
      setActiveCount((prev) => prev - notificationIds.length);
      await markNotificationsAsSeen.mutateAsync(notificationIds);
    } catch (error) {
      setActiveCount((prev) => prev + notificationIds.length);
      logger.error("Failed to mark notifications as seen", {
        operation: "markNotificationsAsSeen",
        error,
        notificationCount: notificationIds.length,
      });
    } finally {
      setIsMarkingAsSeen((prev) => ({
        ...prev,
        isMarking: false,
        prevCount: prev.prevCount - notificationIds.length,
      }));
    }
  };

  const handleDismissAllNotifications = async () => {
    try {
      setIsDismissingAllNotifications(true);
      await dismissAllNotifications.mutateAsync();
      setActiveNotifications([]);
    } catch (error) {
      logger.error("Failed to dismiss all notifications", {
        operation: "dismissAllNotifications",
        error,
      });
    } finally {
      setIsDismissingAllNotifications(false);
    }
  };

  const handleDismissNotification = async (notificationId: string) => {
    setIsDismissingNotification((prev) => ({
      ...prev,
      [notificationId]: true,
    }));
    try {
      await dismissNotification.mutateAsync(notificationId);
      setActiveNotifications((prev) =>
        prev.filter((n) => n.id !== notificationId)
      );
      setAllNotifications((prev) =>
        prev.filter((n) => n.id !== notificationId)
      );
    } catch (error) {
      logger.error("Failed to dismiss notification", {
        operation: "dismissNotification",
        error,
        notificationId,
      });
    } finally {
      setIsDismissingNotification((prev) => {
        const { [notificationId]: _, ...rest } = prev || {};
        return rest;
      });
    }
  };

  const handleDismissNotifications = async (notificationIds: string[]) => {
    if (notificationIds.length === 0) return;
    const idSet = new Set(notificationIds);
    setIsDismissingNotification((prev) => {
      const next = { ...prev };
      notificationIds.forEach((id) => {
        next[id] = true;
      });
      return next;
    });
    try {
      await dismissNotifications.mutateAsync(notificationIds);
      setActiveNotifications((prev) =>
        prev.filter((n) => !idSet.has(n.id))
      );
      setAllNotifications((prev) =>
        prev.filter((n) => !idSet.has(n.id))
      );
    } catch (error) {
      logger.error("Failed to dismiss notifications", {
        operation: "dismissNotifications",
        error,
        notificationCount: notificationIds.length,
      });
    } finally {
      setIsDismissingNotification((prev) => {
        const next = { ...prev };
        notificationIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    }
  };

  const handleDeleteNotifications = async (notificationIds: string[]) => {
    try {
      setIsDeletingNotifications(true);
      await deleteNotifications.mutateAsync(notificationIds);
      setActiveNotifications((prev) =>
        prev.filter((n) => !notificationIds.includes(n.id))
      );
      setAllNotifications((prev) =>
        prev.filter((n) => !notificationIds.includes(n.id))
      );
      setCheckedNotifications((prev) =>
        prev.filter((n) => !notificationIds.includes(n))
      );
    } catch (error) {
      logger.error("Failed to delete notifications", {
        operation: "deleteNotifications",
        error,
        notificationCount: notificationIds.length,
      });
    } finally {
      setIsDeletingNotifications(false);
    }
  };

  const handleDeleteAllNotifications = async () => {
    try {
      setIsDeletingAllNotifications(true);
      await deleteAllNotifications.mutateAsync();
      setActiveNotifications([]);
      setAllNotifications([]);
      setCheckedNotifications([]);
      setTotalNotificationCount(0);
      setAllNotificationsCurrentPage(1);
      setActiveNotificationsCurrentPage(1);
      setHasMoreAll(false);
      setHasMoreActive(false);
      setIsLoadingMoreAll(false);
      setIsLoadingMoreActive(false);
      pendingPageReset.current = false;
    } catch (error) {
      logger.error("Failed to delete all notifications", {
        operation: "deleteAllNotifications",
        error,
      });
    } finally {
      setIsDeletingAllNotifications(false);
    }
  };

  const handleNewNotification = (notification: Notification) => {
    switch (notification.type) {
      case "WorkspaceDocumentUploaded":
      case "WorkspaceDocumentsUploaded":
      case "WorkspaceDocumentUpdated": {
        const { workspaceId, documentId } = parseWorkspaceDocumentEntityPath(
          notification.entityPath
        );
        queryClient.invalidateQueries({
          queryKey: [PATH_KEYS.workspace, workspaceId],
        });
        queryClient.invalidateQueries({
          queryKey: [PATH_KEYS.workspace],
        });
        // Someone may have reindexed this document; other documents only need
        // a refetch if they had no HTML yet.
        invalidateDocumentHtml(queryClient, workspaceId, documentId);
        invalidateMissingDocumentHtml(queryClient);
        break;
      }
      case UsersNotificationType.WorkspaceCommentUpdated:
      case UsersNotificationType.WorkspaceCommentRepliedTo:
      case UsersNotificationType.WorkspaceCommentReacted:
      case UsersNotificationType.WorkspaceCommentCreated: {
        const { workspaceId, fileId } = parseWorkspaceCommentEntityPath(
          notification.entityPath
        );

        queryClient.invalidateQueries({
          queryKey: [PATH_KEYS.comment, workspaceId, fileId],
        });
        queryClient.invalidateQueries({
          queryKey: [PATH_KEYS.comment, "workspace", workspaceId, "general"],
        });
        break;
      }
      case "WorkspaceUpdated": {
        queryClient.invalidateQueries({
          queryKey: [PATH_KEYS.workspace],
        });
        invalidateMissingDocumentHtml(queryClient);
        break;
      }
      default:
        break;
    }
  };

  /* __________________Connection Setup__________________ */
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const startNotificationHubConnection = async () => {
      if (connectionInitialized.current) return;
      connectionInitialized.current = true;
      if (
        hubConnection &&
        hubConnection.state === HubConnectionState.Connected
      ) {
        return; // Exit if there's already an active connection
      }

      setIsLoading(true);
      setConnectionState(ConnectionState.Connecting);
      setTimeout(async () => {
        await createHubConnection(HubType.NotificationHub, negotiate)
          .then(async (connection) => {
            await connection
              .start()
              .then(async () => {
                // Get user ID from token
                const userId = me?.id;
                if (!userId) {
                  throw new Error("Unable to get user ID from token");
                }

                await setContext({
                  sessionId: userId,
                  connectionId: connection.connectionId,
                  hub: HubType.NotificationHub,
                });
                setConnectionState(ConnectionState.Connected);
              })
              .catch((error) => {
                logger.error(
                  "Failed to start the notification SignalR connection",
                  {
                    operation: "startNotificationHubConnection",
                    error,
                  }
                );
                setConnectionState(ConnectionState.Disconnected);
                setIsLoading(false);
                return;
              });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            connection.on("ReceiveNotification", (rawNotification: any) => {
              const notification: Notification = camelcaseKeys(
                rawNotification,
                { deep: true }
              );

              const convertedNotification = {
                ...notification,
                type: convertNotificationType(notification.type),
                status: UsersNotificationSubscriptionStatus.Active,
              };

              setActiveNotifications((prev) => [
                convertedNotification,
                ...prev,
              ]);

              setAllNotifications((prev) => [convertedNotification, ...prev]);
              setTotalNotificationCount((prev) => prev + 1);
              setActiveCount((prev) => prev + 1);
              handleNewNotification(convertedNotification);
            });

            connection.onreconnected(async (connectionId) => {
              if (!connectionId) return;

              const userId = me?.id;
              if (!userId) return;

              await setContext({
                sessionId: userId,
                connectionId: connectionId,
                hub: HubType.NotificationHub,
              });

              setConnectionState(ConnectionState.Connected);
              setIsLoading(false);
            });

            connection.onreconnecting(() => {
              setConnectionState(ConnectionState.Reconnecting);
              setIsLoading(true);
            });

            connection.onclose(() => {
              setConnectionState(ConnectionState.Disconnected);
              setIsLoading(false);
              setHubConnection(null);
            });

            setHubConnection(connection);
            setIsLoading(false);
          })
          .catch((error) => {
            logger.error(
              "Failed to create the notification SignalR connection",
              {
                operation: "createNotificationHubConnection",
                error,
              }
            );
            setTimeout(() => {
              setConnectionState(ConnectionState.Disconnected);
              setIsLoading(false);
              setHubConnection({} as HubConnection);
            }, 5000);
          });
      }, 1000);
    };

    startNotificationHubConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    const handleOffline = () => {
      setConnectionState(ConnectionState.Reconnecting);
      setIsLoading(true);
    };

    const handleOnline = () => {
      manualReconnect();
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* __________________Connection Handlers_________________ */
  const manualReconnect = async () => {
    if (hubConnection) {
      await hubConnection.stop();
    }
    setHubConnection(null);
    connectionInitialized.current = false;
  };

  const contextValue: NotificationHubContextProps = {
    notifications: activeNotifications,
    setNotifications: setActiveNotifications,
    allNotifications,
    setAllNotifications,
    totalNotificationCount,
    activeCount,
    connectionState,
    isLoading,

    manualReconnect,
    seeAllNotifications,
    seeNotifications,
    isMarkingAsSeen: isMarkingAsSeen.isMarking,
    dismissAllNotifications: handleDismissAllNotifications,
    dismissNotification: handleDismissNotification,
    dismissNotifications: handleDismissNotifications,
    isDismissingNotification,
    isDismissingAllNotifications,
    deleteNotifications: handleDeleteNotifications,
    deleteAllNotifications: handleDeleteAllNotifications,
    isDeletingNotifications,
    isDeletingAllNotifications,
    checkedNotifications,
    setCheckedNotifications,
    loadMoreAllNotifications,
    loadMoreActiveNotifications,
    hasMoreAll,
    hasMoreActive,
    isLoadingAllNotifications:
      fetchAllNotifications.isPending && !isLoadingMoreAll,
    isLoadingMoreAll,
    isLoadingMoreActive,
    allNotificationsCurrentPage,
    allNotificationsTotalPages,
    goToAllNotificationsPage,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationHubProvider;
